import React, { useState } from "react";
import { Copy, Download, Mail, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

interface FlightClaimLetterDisplayProps {
  generatedLetter: string;
  companyName: string;
  flightNumber?: string;
}

const FlightClaimLetterDisplay = ({ 
  generatedLetter, 
  companyName, 
  flightNumber 
}: FlightClaimLetterDisplayProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLetter);
      setCopied(true);
      toast.success("Letter copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy letter");
    }
  };

  const handleDownloadAsText = () => {
    const blob = new Blob([generatedLetter], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${companyName}-flight-${flightNumber || 'claim'}-letter.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Letter downloaded");
  };

  const handleOpenInMail = () => {
    const subject = encodeURIComponent(`Flight Compensation Claim - ${flightNumber || 'Flight'}`);
    const body = encodeURIComponent(generatedLetter);
    const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
    window.open(mailtoLink);
  };

  if (!generatedLetter) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          <p>No claim letter has been generated yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 justify-end">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleCopyToClipboard}
          className="flex items-center gap-2"
        >
          {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied!" : "Copy to Clipboard"}
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleDownloadAsText}
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Download as Text
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleOpenInMail}
          className="flex items-center gap-2"
        >
          <Mail className="h-4 w-4" />
          Open in Mail
        </Button>
      </div>

      {/* Letter Content */}
      <Card>
        <CardContent className="p-6">
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-wrap font-mono text-sm leading-relaxed">
              {generatedLetter}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FlightClaimLetterDisplay;